import { from, Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { Reservations } from '../api';
import { PaymentMethodStore, paymentMethodStore } from './payment-method.store';
import { Name, Address } from '../models.common';
import { applyTransaction } from '@datorama/akita';
import { AcceptCardData, getPaymentNonce } from '../utils';
import { dispatchForm } from '../forms';
import { PaymentMethodForms } from './payment-method.forms';

export class PaymentMethodService {
  // NOTE: This service is a module-scoped singleton (see `paymentMethodService`
  // export at the bottom of this file). The request-id + subscription tracking
  // below assumes a single logical consumer at a time for each fetcher.
  //
  // If two components mount simultaneously and both call `getGuestPaymentMethods`
  // (or both call `getPaymentMethods`) with *different* arguments, the second
  // call will cancel the first and the store will only reflect the latest
  // request's args. That is the desired behavior when the same component
  // changes its inputs, but unrelated consumers on screen at once would
  // effectively fight over the store.
  //
  // No such concurrent-consumer case exists today. If that changes, consider
  // either keying the store by (userId, propertyId, corporateAccountId) or
  // making this service instance-scoped per consumer.

  // Track the latest in-flight request for each fetcher so stale responses
  // (e.g. from a previous user/corporate account selection) cannot overwrite
  // the store after a newer request has been issued.
  private getPaymentMethodsRequestId = 0;
  private getGuestPaymentMethodsRequestId = 0;

  // Track active subscriptions so we can cancel them when a new request comes in.
  private getPaymentMethodsSub?: Subscription;
  private getGuestPaymentMethodsSub?: Subscription;

  constructor(
    private store: PaymentMethodStore,
    private readonly guestApi: Reservations.GuestApi,
    private readonly guestsApi: Reservations.GuestsApi
  ) {}

  getPaymentMethods(propertyId?: string, corporateAccountId?: string): void {
    // Cancel any in-flight request and bump the request id.
    this.getPaymentMethodsSub?.unsubscribe();
    const requestId = ++this.getPaymentMethodsRequestId;

    this.getPaymentMethodsSub = from(
      this.guestApi.guestPaymentmethodsGet(propertyId, corporateAccountId)
    )
      .pipe(map(({ data }) => data))
      .subscribe({
        next: ({ data, invoiceAccounts }) => {
          // If a newer request has started since we fired this one, ignore the result.
          if (requestId !== this.getPaymentMethodsRequestId) return;

          applyTransaction(() => {
            this.store.set(data);
            this.store.update(() => ({ invoiceAccounts }));
          });
        },
        error: () => {
          // Clear stale data on error so the UI doesn't show the previous
          // property/corporate account's payment methods. Only clear if this
          // is still the latest request.
          if (requestId !== this.getPaymentMethodsRequestId) return;
          this.store.reset();
        },
      });
  }

  getPaymentMethodsForProfile(propertyId?: string, corporateAccountId?: string): void {
    // Cancel any in-flight request and bump the request id.
    this.getPaymentMethodsSub?.unsubscribe();
    const requestId = ++this.getPaymentMethodsRequestId;

    this.getPaymentMethodsSub = from(
      this.guestApi.guestPaymentmethodManageGet(propertyId, corporateAccountId)
    )
      .pipe(map(({ data }) => data))
      .subscribe({
        next: ({ data }) => {
          // If a newer request has started since we fired this one, ignore the result.
          if (requestId !== this.getPaymentMethodsRequestId) return;

          applyTransaction(() => {
            this.store.set(data);
          });
        },
        error: () => {
          // Clear stale data on error so the UI doesn't show the previous
          // property/corporate account's payment methods. Only clear if this
          // is still the latest request.
          if (requestId !== this.getPaymentMethodsRequestId) return;
          this.store.reset();
        },
      });
  }

  getGuestPaymentMethods(userId: string, propertyId: string, corporateAccountId?: string): void {
    this.getGuestPaymentMethodsSub?.unsubscribe();
    const requestId = ++this.getGuestPaymentMethodsRequestId;

    this.getGuestPaymentMethodsSub = from(
      this.guestsApi.guestsGuestUserIdPaymentmethodsGet(userId, propertyId, corporateAccountId)
    )
      .pipe(map(({ data }) => data))
      .subscribe({
        next: ({ data, invoiceAccounts }) => {
          if (requestId !== this.getGuestPaymentMethodsRequestId) return;

          applyTransaction(() => {
            this.store.set(data);
            this.store.update(() => ({ invoiceAccounts }));
          });
        },
        error: () => {
          // Clear stale data on error so the UI doesn't show the previous
          // guest's payment methods. Only clear if this is still the latest
          // request.
          if (requestId !== this.getGuestPaymentMethodsRequestId) return;
          this.store.reset();
        },
      });
  }

  updatePaymentMethod(
    paymentProfileId: string,
    name: Name,
    address: Address,
    defaultMethod: boolean
  ): void {
    from(
      this.guestApi.guestPaymentmethodPaymentProfileIdPost(paymentProfileId, {
        name,
        address,
        defaultMethod,
      })
    ).subscribe(() => this.getPaymentMethods());
  }

  addPaymentMethod(name: Name, address: Address, card?: AcceptCardData): void {
    this.addPaymentMethodObs(name, address, card)
      .pipe(
        dispatchForm(PaymentMethodForms.AddPaymentMethod),
        map(response => response.data.data)
      )
      .subscribe(paymentMethod =>
        applyTransaction(() => {
          this.store.upsert(paymentMethod.authNetPaymentId, paymentMethod);
        })
      );
  }

  addPaymentMethodObs(name: Name, address: Address, card?: AcceptCardData) {
    return getPaymentNonce(card).pipe(
      switchMap(nonce =>
        this.guestApi.guestPaymentmethodPost({
          name,
          address,
          paymentNonce: nonce?.opaqueData,
        })
      )
    );
  }

  deletePaymentMethod(paymentProfileId: string, paymentCustomerId?: string): void {
    from(
      this.guestApi.guestPaymentmethodPaymentProfileIdDelete(paymentProfileId, {
        paymentCustomerId: paymentCustomerId ?? undefined,
      })
    )
      .pipe(dispatchForm(PaymentMethodForms.DeletePaymentMethod))
      .subscribe(() => this.store.remove(paymentProfileId));
  }

  reset(): void {
    // Cancel any in-flight fetches when resetting so their responses cannot
    // populate the store after it has been cleared.
    this.getPaymentMethodsSub?.unsubscribe();
    this.getGuestPaymentMethodsSub?.unsubscribe();
    this.getPaymentMethodsRequestId++;
    this.getGuestPaymentMethodsRequestId++;
    this.store.reset();
  }
}

export const paymentMethodService = new PaymentMethodService(
  paymentMethodStore,
  new Reservations.GuestApi(),
  new Reservations.GuestsApi()
);
